import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import { fetchVehiclePriceEvents } from "../../../../services/vehiclePriceApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RowActionMenu from "../layout/RowActionMenu";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatCop(value) {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return String(value);
  }
}

function vehicleLabel(row) {
  return [row.makeNormalized, row.modelNormalized, row.year].filter(Boolean).join(" ") || "—";
}

const VehiclePriceEventsPage = ({ onClose, onNavigate }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW);

  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [eventType, setEventType] = useState("");
  const [vehicleSourceId, setVehicleSourceId] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [eventFrom, setEventFrom] = useState("");
  const [eventTo, setEventTo] = useState("");
  const [minDeltaPct, setMinDeltaPct] = useState("");
  const [minDeltaCop, setMinDeltaCop] = useState("");

  const loadEvents = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchVehiclePriceEvents({
        eventType: eventType || undefined,
        vehicleSourceId: vehicleSourceId || undefined,
        sellerType: sellerType || undefined,
        city: city.trim() || undefined,
        department: department.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        eventFrom: eventFrom || undefined,
        eventTo: eventTo || undefined,
        minDeltaPct: minDeltaPct ? Number(minDeltaPct) : undefined,
        minDeltaCop: minDeltaCop ? Number(minDeltaCop) : undefined,
        limit: 200,
      });
      setEvents(data);
    } catch (error) {
      setEvents([]);
      setErrorMessage(error.message || "Unable to load price events.");
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    eventType,
    vehicleSourceId,
    sellerType,
    city,
    department,
    make,
    model,
    eventFrom,
    eventTo,
    minDeltaPct,
    minDeltaCop,
  ]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <div className="admin-dashboard admin-vehicle-price-events-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Vehicle Price Events</h1>
          <p className="admin-dashboard__subtitle">
            Price changes and listing signals from the observation engine.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={loadEvents}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-source-controls-dash__filters admin-vehicle-sources-dash__filters">
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All event types</option>
          <option value="NEW_LISTING">New listing</option>
          <option value="PRICE_DECREASE">Price decrease</option>
          <option value="PRICE_INCREASE">Price increase</option>
          <option value="MILEAGE_CHANGED">Mileage changed</option>
          <option value="STATUS_CHANGED">Status changed</option>
        </select>
        <select value={vehicleSourceId} onChange={(e) => setVehicleSourceId(e.target.value)}>
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.sourceName}
            </option>
          ))}
        </select>
        <input type="text" placeholder="Make" value={make} onChange={(e) => setMake(e.target.value)} />
        <input
          type="text"
          placeholder="Model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
        />
        <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <input
          type="text"
          placeholder="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <select value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
          <option value="">All seller types</option>
          <option value="DEALER">Dealer</option>
          <option value="MARKETPLACE">Marketplace</option>
        </select>
        <input type="date" value={eventFrom} onChange={(e) => setEventFrom(e.target.value)} />
        <input type="date" value={eventTo} onChange={(e) => setEventTo(e.target.value)} />
        <input
          type="number"
          placeholder="Min delta %"
          value={minDeltaPct}
          onChange={(e) => setMinDeltaPct(e.target.value)}
        />
        <input
          type="number"
          placeholder="Min delta COP"
          value={minDeltaCop}
          onChange={(e) => setMinDeltaCop(e.target.value)}
        />
      </section>

      {loading ? <AdminStatePanel variant="loading" message="Loading price events…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadEvents} />
      ) : null}

      {!loading && !errorMessage ? (
        <AdminTableContainer>
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Vehicle</th>
                <th>Source</th>
                <th>Previous</th>
                <th>New</th>
                <th>Delta COP</th>
                <th>Delta %</th>
                <th>Event at</th>
                <th>Seller</th>
                <th>City</th>
                <th>Run</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={12} className="muted">
                    No price events match the current filters.
                  </td>
                </tr>
              ) : (
                events.map((row) => (
                  <tr key={row.id}>
                    <td>{row.eventType}</td>
                    <td>{vehicleLabel(row)}</td>
                    <td>{row.sourceName || row.sourceKey}</td>
                    <td>{formatCop(row.previousPriceCop)}</td>
                    <td>{formatCop(row.newPriceCop)}</td>
                    <td>{row.priceDeltaCop != null ? formatCop(row.priceDeltaCop) : "—"}</td>
                    <td>{row.priceDeltaPct != null ? `${row.priceDeltaPct}%` : "—"}</td>
                    <td>{formatDate(row.eventAt)}</td>
                    <td>{row.sellerType || "—"}</td>
                    <td>{row.city || "—"}</td>
                    <td className="muted">{row.ingestionRunId ? row.ingestionRunId.slice(0, 8) : "—"}</td>
                    <td>
                      <RowActionMenu
                        items={[
                          {
                            label: "Open source URL",
                            onClick: () => window.open(row.sourceUrl, "_blank", "noopener"),
                            disabled: !row.sourceUrl,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableContainer>
      ) : null}
    </div>
  );
};

export default VehiclePriceEventsPage;
